"use client";

import { FC } from "react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import { PrismicNextImage } from "@prismicio/next";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * Props for `StartHealthcareJourneySection`.
 */
export type StartHealthcareJourneySectionProps =
  SliceComponentProps<Content.StartHealthcareJourneySectionSlice>;

/**
 * Component for "StartHealthcareJourneySection" Slices.
 */
const StartHealthcareJourneySection: FC<StartHealthcareJourneySectionProps> = ({
  slice,
}) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#FAFDFF] py-[60px] px-4 md:px-[30px] lg:px-[60px] xl:px-20 flex flex-col items-center justify-center gap-4"
    >
      <div className="bg-[#D9F0FF] rounded-[40px] flex items-center gap-2.5 p-3.5">
        <PrismicNextImage field={slice.primary.badge_icon} />
        <span className="text-[#033D61] font-merriweather text-lg font-normal leading-7">{slice.primary.badge_content}</span>
      </div>
      <motion.div
        className="font-archivo text-[#033D61] text-4xl text-left md:text-center font-bold leading-12 md:text-5xl md:font-semibold md:leading-16"
        initial={{ opacity: 0, y: -30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <PrismicRichText
          field={slice.primary.title}
          components={{
            label: ({ node, children }) => {
              if (node.data.label === "Highlight") {
                return <span className="text-secondary">{children}</span>;
              }
              return <span className="text-primary">{children}</span>;
            },
          }}
        />
      </motion.div>
      <motion.div
        className="md:text-center text-[#033D61] font-merriweather font-normal leading-8 text-xl text-left mb-12"
        initial={{ opacity: 0, y: -30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <PrismicRichText
          field={slice.primary.sub_title}
          components={{
            label: ({ node, children }) => {
              if (node.data.label === "Highlight") {
                return <span className="text-secondary">{children}</span>;
              }
              return <span className="text-primary">{children}</span>;
            },
          }}
        />
      </motion.div>
      <motion.div
        className="grid grid-cols-1 lg:grid-cols-3 gap-[18px] w-full max-w-7xl"
        initial={{ opacity: 0, y: -30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        {slice.primary.contents.map(content => (
          <div key={content.title} className="h-full px-9 py-[42px] bg-white border border-[#9CD6FA] rounded-[10px]">
            <div className="w-12 h-12 rounded-full bg-[#EBF7FF] flex items-center justify-center mb-8">
              <PrismicNextImage field={content.icon} />
            </div>
            <div className="text-[#033D61] font-archivo text-2xl font-semibold leading-9 mb-[15px]">{content.title}</div>
            <div className="text-[#033D61] font-merriweather text-lg font-normal leading-7">{content.description}</div>
          </div>
        ))}
      </motion.div>
    </section>
  );
};

export default StartHealthcareJourneySection;
